import axios from 'axios';

import { API_SERVER, ACCESS_TOKEN, REFRESH_TOKEN } from '../../settings/constants';
import webStorageClient from '../../utils/webStorageClient';

const axiosInstance = axios.create({
  baseURL: API_SERVER,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 600000
});

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const subscribeTokenRefresh = (cb: (token: string) => void) => {
  refreshSubscribers.push(cb);
};

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach(cb => cb(token));
  refreshSubscribers = [];
};

const handleLogoutAndRedirect = () => {
  const isLoginPage = window.location.pathname === '/auth/login';
  const alreadyRedirecting = (window as any).__isRedirectingToLogin;

  if (!isLoginPage && !alreadyRedirecting) {
    (window as any).__isRedirectingToLogin = true;
    webStorageClient.removeAll();
    localStorage.clear();
    window.location.replace('/auth/login');
  }
};

axiosInstance.interceptors.request.use(
  function (config) {
    const token = webStorageClient.getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  function (error) {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response: any) => {
    return response?.data;
  },
  async (error: any) => {
    const { config, response } = error;
    const originalRequest = config;

    if (response?.status === 401 && originalRequest && !originalRequest._retry) {
      // If it is the login or refresh request itself failing with 401, logout immediately
      if (originalRequest.url?.includes('/api/login/') || originalRequest.url?.includes('/api/refresh/')) {
        handleLogoutAndRedirect();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = webStorageClient.get(REFRESH_TOKEN);
      if (!refreshToken) {
        isRefreshing = false;
        handleLogoutAndRedirect();
        return Promise.reject(error);
      }

      try {
        const refreshResponse = await axios.post(`${API_SERVER}/api/refresh/`, {
          refresh: refreshToken
        });

        const newAccessToken = refreshResponse.data.access;
        const newRefreshToken = refreshResponse.data.refresh;

        webStorageClient.set(ACCESS_TOKEN, newAccessToken);
        if (newRefreshToken) {
          webStorageClient.set(REFRESH_TOKEN, newRefreshToken);
        }

        isRefreshing = false;
        onRefreshed(newAccessToken);

        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        handleLogoutAndRedirect();
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
